import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useCreateItemMutation,
  useDeleteItemMutation,
  useGetItemByIdQuery,
  useUpdateItemMutation,
} from '../../../slices/simpleApiSlice';

//mui
import {
  Alert,
  Badge,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';

//иконки
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

//components
import ScreenContainer from '../../ScreenContainer';
import Alerton from '../../Alerton';
import RelatedList from '../../RelatedList';
import FieldInput from '../../Common/UI/FieldInput/FieldInput';
import AddDrawer from '../../AddDrawer';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
      sx={{ p: 0, m: 0 }}
    >
      {value === index && <Box sx={{ p: 0, m: 0 }}>{children}</Box>}
    </div>
  );
}

// Помощник для чтения вложенных полей (например 'dates.plannedEnd')
const getFieldValue = (obj, path) => {
  if (!obj) return undefined;
  // Если ключ уже плоский (сработал onChange)
  if (obj[path] !== undefined) return obj[path];
  // Если ключ вложенный (пришли данные из БД)
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const ItemViewer = ({
  slug,
  id,
  actions = ['add', 'edit', 'delete', 'close', 'redirect_after_update'],
  onClose,
  appBarSX,
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // console.log('state', state);

  //   const { slug, id } = useParams();
  const [message, setMessage] = useState('');
  const [editMode, setEditMode] = useState(id === 'new' ? true : false);
  const [inputs, setInputs] = useState({});
  const [addDrawerOptions, setAddDrawerOptions] = useState({ open: false });

  // useEffect(() => {
  //   console.log('addDrawerOptions', addDrawerOptions);
  // }, [addDrawerOptions]);

  const [showAllFields, setShowAllFields] = useState(false);
  const theme = useTheme(); // MUI theme hook

  const hasValue = (val) => {
    if (typeof val === 'boolean') return val === true;
    if (val === null || val === undefined || val === '') return false;
    if (Array.isArray(val)) return val.length > 0; // [] = false
    if (typeof val === 'object' && val !== null)
      return Object.keys(val).length > 0; // {} = false
    return val.toString().trim() !== ''; // " " = false
  };


  const isFieldVisible = (field, inputs, showAll = false) => {
    if (showAll) return true;
    if (field.hidden) return false;

    const priority = field.priority ?? 10;
    if (priority <= 2) return true;
    

    // ИСПОЛЬЗУЕМ НАШ ПОМОЩНИК
    const val = getFieldValue(inputs, field.field);
    if (priority <= 9) return hasValue(val);

    return false;
  };

  const queryUrl = `/api/directories/${slug}`;
  const directoryTag = `Directory-${slug.charAt(0).toUpperCase() + slug.slice(1)}`;
  const listTag = `${directoryTag}-List`;
  // console.log('slug', slug, 'tags:', directoryTag, listTag);

  const { data, refetch, isLoading, isFetching, isError, error } =
    useGetItemByIdQuery(
      {
        id,
        url: queryUrl + '/' + id,
        providesTags: [listTag],
      },
      { skip: !id },
    );

  useEffect(() => {
    console.log('inputs', inputs);
  }, [inputs]);

  useEffect(() => {
    console.log('ItemViewer data?.item', data?.item);
    if (data?.item) {
      setInputs(data?.item);
    }
    if (state && Object.keys(state).length > 0) {
      Object.entries(state).forEach(([key, value]) => {
        setInputs((prev) => ({
          ...prev,
          [key]: value,
        }));
      });
    }
  }, [data, state]);

  const [createItem, { isLoading: isLoadingCreate }] = useCreateItemMutation();
  const [updateItem, { isLoading: isLoadingUpdate }] = useUpdateItemMutation();
  const [deleteItem] = useDeleteItemMutation();

  const { userInfo, permissions } = useSelector((state) => state.auth);
  const canRead = userInfo?.isAdmin || !!permissions?.directories?.[slug]?.read;
  const canCreate =
    userInfo?.isAdmin || !!permissions?.directories?.[slug]?.create;
  const canUpdate =
    userInfo?.isAdmin || !!permissions?.directories?.[slug]?.update;
  const canDelete =
    userInfo?.isAdmin || !!permissions?.directories?.[slug]?.delete;
  const disabled = !canUpdate || !editMode;

  const deleteHandler = async () => {
    if (!canDelete) return;
    if (actions.includes('delete') === false) return;
    if (window.confirm('Вы уверены ?')) {
      try {
        await deleteItem({
          url: queryUrl + '/' + id,
          invalidatesTags: [directoryTag, listTag],
        }).unwrap();
        refetch();
      } catch (error) {
        setMessage(error?.data?.message || error?.message || error.error);
      }
    }
  };

  const createUpdateHandler = async () => {
    if (id === 'new' && !canCreate) return;
    if (id !== 'new' && !canUpdate) return;

    if (actions.includes('edit') === false) return;

    if (!editMode) {
      setEditMode(true);
      return;
    }

    const formData = new FormData();

    // Проходим по всем значениям в inputs
    Object.entries(inputs).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return;
      }

      if (value instanceof File) {
        // Новое изображение → добавляем как файл
        formData.append(key, value);
      } else if (typeof value === 'string' && value.trim() !== '') {
        // Существующий URL или текстовое поле
        formData.append(key, value);
      } else if (typeof value === 'number') {
        formData.append(key, String(value));
      } else if (Array.isArray(value)) {
        // Если поле — массив (файлы, строки или ОБЪЕКТЫ)
        if (value.length === 0) {
          // Опционально: если хотим явно очистить массив на сервере
          // formData.append(key, JSON.stringify([])); 
        } else {
          value.forEach((item) => {
            if (item instanceof File) {
              formData.append(`${key}[]`, item);
            } else if (typeof item === 'string') {
              formData.append(`${key}[]`, item);
            } else if (typeof item === 'object' && item !== null) {
              // ✅ ИСПРАВЛЕНИЕ: Если в массиве объекты (как в watchers), достаем их _id
              const itemId = item._id || item.id;
              if (itemId) {
                formData.append(`${key}[]`, itemId);
              } else {
                formData.append(`${key}[]`, JSON.stringify(item));
              }
            }
          });
        }
      } else if (typeof value === 'object' && value !== null) {
        // Одиночные объекты (например assignee)
        const id = value._id || value.id;
        if (id) {
          formData.append(key, id);
        } else {
          formData.append(key, JSON.stringify(value));
        }
      } else {
        // boolean и прочее
        formData.append(key, value);
      }
    });

    // Теперь payload с body: formData
    const payload = {
      url: queryUrl + '/' + id,
      invalidatesTags: [directoryTag, listTag],
      body: formData,
    };

    // console.log('FormData entries:', Array.from(formData.entries()));

    try {
      let result;

      if (id === 'new') {
        result = await createItem(payload).unwrap();
      } else {
        // Вариант 1: если мутация принимает formData напрямую
        result = await updateItem(payload).unwrap();

        // Вариант 2: если мутация ожидает объект с id и body
        // result = await updateItem({ id, body: formData }).unwrap();
      }

      // Сброс локальных файлов после успешной загрузки (опционально)
      const clearedInputs = { ...inputs };
      Object.keys(clearedInputs).forEach((key) => {
        if (clearedInputs[key] instanceof File) {
          clearedInputs[key] = result?.[key] || ''; // или оставить как есть
        }
      });
      setInputs(clearedInputs);

      if (result) {
        const itemId = result?._id ?? result?.id;
        if (itemId) {
          if (actions.includes('redirect_after_update')) {
            navigate(`/directories/${slug}/${itemId}`, {
              state: { backUrl: state?.backUrl },
            });
          } else {
            refetch();
          }
        } else {
          console.warn('No id or _id in result', result);
          // Здесь можно refetch(), если очень хочется остаться на странице
          // но лучше показать ошибку пользователю
        }
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setMessage(error?.data?.message || error.error || 'Не удалось сохранить');
    }

    setEditMode(false);
  };

  const onChangeHandler = useCallback((key, value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  }, []); // пустой массив = функция стабильна навсегда

  const handleAddDrawerSelect = (target, data) => {
    let newValue;

    if (addDrawerOptions.multiple) {
      // ✅ Защита: если там вдруг объект (из-за прошлого бага), делаем из него массив
      let current = inputs[target] || [];
      if (!Array.isArray(current)) {
        current = [current];
      }

      // Проверяем, нет ли уже такого
      const exists = current.some((item) => item._id === data._id);
      if (exists) {
        console.log('Уже выбран');
        setAddDrawerOptions((prev) => ({ ...prev, open: false }));
        return;
      }

      newValue = [...current, data]; // Добавляем в массив
    } else {
      newValue = data; // Одиночный выбор
    }

    onChangeHandler(target, newValue);
    setAddDrawerOptions((prev) => ({ ...prev, open: false }));
  };

  const processedTabs = useMemo(() => {
    if (!data?.metadata?.tabs?.length || id === 'new') return [];
    return data.metadata.tabs.map((tab) => ({
      ...tab,
      ownerId: id,
    }));
  }, [data?.metadata?.tabs, id]); // onChangeHandler не меняется!

  if (!canRead) {
    return (
      <Alert severity='warning' sx={{ width: '100%' }}>
        Недостаточно прав
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  } else if (isError) {
    return (
      <Alert severity='error' sx={{ width: '100%' }}>
        {error?.data?.message || error?.error || error?.message}
      </Alert>
    );
  }

  const buttons = [];
  if (
    ((id === 'new' && canCreate) || (id !== 'new' && canUpdate)) &&
    actions.includes('edit')
  ) {
    buttons.push({
      title: 'Редактировать',
      onClick: () => setEditMode(!editMode),
      type: 'icon',
      icon: <EditNoteIcon />,
      xs: 2,
      position: 'left',
    });
  }
  if (
    ((id === 'new' && canCreate) || (id !== 'new' && canUpdate)) &&
    editMode &&
    actions.includes('edit')
  ) {
    buttons.push({
      title: 'Сохранить',
      onClick: createUpdateHandler,
      type: 'icon',
      icon: <SaveIcon color='success' />,
      xs: 2,
      position: 'left',
    });
  }

  if (canDelete && id !== 'new' && editMode && actions.includes('delete')) {
    buttons.push({
      title: 'Пометить на удаление',
      onClick: deleteHandler,
      type: 'icon',
      icon: inputs?.visible ? (
        <DeleteForeverIcon />
      ) : (
        <RestoreFromTrashIcon color='warning' />
      ),
      xs: 2,
      position: 'left',
    });
  }

  if (actions.includes('close')) {
    buttons.push({
      title: 'Закрыть',
      onClick: () => {
        if (typeof onClose === 'function') {
          onClose();
        } else {
          if (state?.backUrl) navigate(state.backUrl);
          else navigate(`/directories/${slug}`);
        }
      },
      type: 'icon',
      icon: <CloseIcon />,
      xs: 2,
      position: 'right',
    });
  }

  const hiddenFieldsCount =
    data?.metadata?.fields?.filter(
      (field) => !field.hidden && !isFieldVisible(field, inputs, false), // Считаем скрытые при showAll=false
    )?.length || 0;

  return (
    <>
      <ScreenContainer
        appBarSX={appBarSX}
        buttons={buttons}
        isProgress={isFetching || isLoadingCreate || isLoadingUpdate}
      >
        <Card>
          <CardHeader
            title={data?.metadata?.title || 'Элемент'}
            subtitle={id === 'new' ? 'Новый' : 'Редактирование'}
            action={
              !showAllFields && hiddenFieldsCount > 0 ? ( // Только если есть скрытые
                <IconButton
                  size='small'
                  onClick={() => setShowAllFields(true)} // Только показать (не toggle)
                  title='Показать остальные поля'
                >
                  <Badge
                    badgeContent={hiddenFieldsCount} // Число на бейдже
                    color='primary'
                    sx={{ mr: 1 }}
                  >
                    <ExpandMoreIcon />
                  </Badge>
                </IconButton>
              ) : showAllFields ? ( // Свернуть обратно
                <IconButton
                  size='small'
                  onClick={() => setShowAllFields(false)}
                  title='Скрыть остальные поля'
                >
                  <ExpandLessIcon />
                </IconButton>
              ) : null
            }
          />

          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              {data?.metadata?.fields
                ?.filter((field) => !field.hidden)
                ?.filter((field) =>
                  isFieldVisible(field, inputs, showAllFields),
                )
                ?.map((field) => (
                  <React.Fragment key={field.field}>
                    {!field.columns ? (
                      <Grid item xs={field.width || 12} sm={field.width || 12}>
                        <FieldInput
                          maxRows={field?.maxRows || 10}
                          minRows={field?.minRows || 1}
                          multiline={field?.multiline || false}
                          multiple={field?.multiple || false}
                          options={field?.options || []}
                          disabled={disabled || !field.editable}
                          field={field}
                          // value={
                          //   inputs[field.field] instanceof File
                          //     ? inputs[field.field]
                          //     : inputs[field.field]?.title ||
                          //       inputs[field.field]?.name ||
                          //       inputs[field.field] ||
                          //       ''
                          // }
                          value={(() => {
                            const rawVal = getFieldValue(inputs, field.field);
                            if (rawVal instanceof File) return rawVal;
                            return rawVal?.title || rawVal?.name || rawVal || '';
                          })()}
                          onChange={onChangeHandler} // connect to form
                          handleOpenDrawer={() =>
                            setAddDrawerOptions({
                              open: true,
                              target: field.field,
                              multiple: field.multiple || false,
                              ...(field?.addOptions || {}),
                            })
                          }
                        />
                      </Grid>
                    ) : data?.item?.[field.field] &&
                      Array.isArray(field.columns) &&
                      field.columns.length > 0 ? (
                      <Grid item xs={12} sm={field.width || 12}>
                        <Typography
                          variant='subtitle2'
                          fontWeight={600}
                          gutterBottom
                          sx={{ mb: 2 }}
                        >
                          {field.label}
                        </Typography>
                        <GridTableList
                          data={{
                            rows: data?.item?.[field.field] || [],
                            metadata: {
                              showheader: field.showheader,
                              columns: field.columns,
                            },
                          }}
                        />
                      </Grid>
                    ) : null}
                  </React.Fragment>
                ))}
            </Grid>
          </CardContent>
          {data?.metadata?.tabs &&
            data?.metadata?.tabs.length > 0 &&
            id !== 'new' && (
              <CardContent sx={{ px: 0, px: 2 }}>
                <TabViewer
                  readOnly={disabled}
                  onChange={onChangeHandler}
                  setAddDrawerOptions={setAddDrawerOptions}
                  tabs={processedTabs}
                  inputs={inputs} // ✅ Передаем отдельно
                  theme={theme}
                  slug={slug}
                  id={id}
                />
              </CardContent>
            )}
        </Card>
      </ScreenContainer>
      <Alerton message={message} handleCloseMessage={() => setMessage('')} />
      {addDrawerOptions?.open && (
        <AddDrawer
          open={addDrawerOptions?.open}
          onClose={() => setAddDrawerOptions(false)}
          title={`Подбор`}
          id={id}
          titleKey={addDrawerOptions?.titleKey}
          addSlug={addDrawerOptions.slug}
          filter={addDrawerOptions?.filter || []}
          existingIds={
            addDrawerOptions.multiple
              // ✅ Бронебойная проверка: если не массив, возвращаем пустой
              ? (Array.isArray(inputs[addDrawerOptions.target])
                  ? inputs[addDrawerOptions.target]
                  : []
                ).map((item) => item._id)
              : []
          }
          onSelect={(data) => {
            // console.log('AddDrawer data', data);

            handleAddDrawerSelect(addDrawerOptions?.target, data);
          }}
        />
      )}
    </>
  );
};

export default ItemViewer;

const GridTableList = ({ data }) => {
  const columns = data.metadata?.columns || [];
  const showheader = data.metadata?.showheader;
  if (!data.rows?.length) {
    return <Typography color='text.secondary'>Нет данных</Typography>;
  }

  //   console.log('showheader', showheader);

  return (
    <>
      {/* Header */}
      {showheader && (
        <Grid container sx={{ py: 1, borderRadius: 1, mb: 1 }}>
          {columns.map((col) => (
            <Grid item xs={col.width} key={col.field} sx={{ px: 1.5 }}>
              <Typography variant='caption' fontWeight={700}>
                {col.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Rows */}
      {data.rows.map((row, idx) => (
        <Grid
          key={idx}
          container
          sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {columns.map((col) => (
            <Grid item xs={col.width} key={col.field} sx={{ px: 1.5 }}>
              <Typography variant='body2'>{row[col.field] || '-'}</Typography>
            </Grid>
          ))}
        </Grid>
      ))}
    </>
  );
};

const TabViewer = ({
  tabs,
  readOnly,
  onChange,
  setAddDrawerOptions,
  inputs,
  theme,
  slug,
  id,
  sx = {},
}) => {
  const [navIndex, setNavIndex] = useState(0);
  const handleChange = (event, newValue) => {
    setNavIndex(newValue);
  };

  return (
    <>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          m: 0,
          p: 0,
          ...sx,
        }}
      >
        <Tabs
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          value={navIndex}
          onChange={handleChange}
          aria-label='basic tabs'
          sx={{
            m: 0,
            p: 0,
          }}
        >
          {tabs.map((el, index) => (
            <Tab
              // sx={{ m: 0, p: 0 }}
              label={el?.label}
              key={index}
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>
      {tabs.map((tabConfig, index) => {
        if (tabConfig.type === 'fields') {
          // console.log('tabConfig', tabConfig);
          return (
            <TabPanel
              key={tabConfig.id || tabConfig.label || index}
              value={navIndex}
              index={index}
            >
              <Grid container spacing={1} sx={{ m: 0, pt: 2 }}>
                {tabConfig?.fields
                  ?.filter((field) => !field.hidden)
                  ?.map((field) => (
                    <React.Fragment key={field.field}>
                      {/* Простые поля */}
                      {!field.columns && (
                        <Grid
                          item
                          xs={field.width || 12}
                          sm={field.width || 12}
                        >
                          <FieldInput
                            key={field.field} // ← вот это очень важно!
                            theme={theme}
                            maxRows={field?.maxRows || 10}
                            minRows={field?.minRows || 1}
                            multiline={field.multiline || false}
                            multiple={field.multiple || false}
                            disabled={readOnly || !field.editable}
                            field={field}
                            // value={
                            //   inputs[field.field] instanceof File
                            //     ? inputs[field.field]
                            //     : inputs[field.field]?.title ||
                            //       inputs[field.field]?.name ||
                            //       inputs[field.field] ||
                            //       ''
                            // }
                           value={(() => {
                              const rawVal = getFieldValue(inputs, field.field);
                              if (rawVal instanceof File) return rawVal;
                              return rawVal?.title || rawVal?.name || rawVal || '';
                            })()}
                            // value={inputs[field.field]}
                            onChange={onChange} // connect to form
                            handleOpenDrawer={() =>
                              setAddDrawerOptions({
                                open: true,
                                target: field.field,
                                multiple: field.multiple || false,
                                ...(field?.addOptions || {}),
                              })
                            }
                          />
                        </Grid>
                      )}
                    </React.Fragment>
                  ))}
              </Grid>
            </TabPanel>
          );
        } else {
          return (
            <TabPanel key={index} value={navIndex} index={index}>
              <RelatedList
                sx={{ m: 0, p: 0 }}
                readOnly={readOnly}
                config={tabConfig}
                ownerId={tabConfig.ownerId}
                item={inputs}
                backUrl={`/directories/${slug}/${id}`}
              />
            </TabPanel>
          );
        }
      })}
    </>
  );
};
